<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  fieldKey: {
    type: String,
    required: true,
  },
  field: {
    type: Object,
    required: true,
  },
  fieldLabel: {
    type: String,
    required: true,
  },
  fieldIcon: {
    type: String,
    required: true,
  },
  availableFields: {
    type: Array,
    required: true,
  },
  templateType: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['remove', 'update:type', 'update:field']);

const { t } = useI18n();

const isCardTemplate = computed(() => props.templateType === 'card');

const updateType = type => {
  emit('update:type', props.fieldKey, type);
};

const updateFieldValue = (property, value) => {
  emit('update:field', props.fieldKey, property, value);
};
</script>

<template>
  <div class="group flex items-start space-x-2">
    <button
      class="flex text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-all duration-200 opacity-0 group-hover:opacity-100 w-6 h-6 p-0 items-center justify-center"
      @click="emit('remove', fieldKey)"
    >
      <Icon icon="i-lucide-trash-2" class="w-4 h-4" />
    </button>

    <div class="flex-1 space-y-2">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <div
            class="flex items-center justify-center w-6 h-6 rounded-md bg-slate-50 dark:bg-slate-800"
          >
            <Icon
              :icon="fieldIcon"
              class="w-4 h-4 text-slate-600 dark:text-slate-400"
            />
          </div>
          <span class="font-bold text-slate-700 dark:text-slate-300">
            {{ fieldLabel }}
          </span>
        </div>
        <div
          v-if="isCardTemplate"
          class="flex rounded bg-slate-50 dark:bg-slate-800 p-0.5"
        >
          <button
            class="px-2 py-0.5 text-xs font-semibold rounded transition-all w-10"
            :class="[
              field.type === 'prompt'
                ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200',
            ]"
            @click="updateType('prompt')"
          >
            {{ t('LIBRARIES.TEMPLATE_CONFIG.INPUT_TYPES.AI') }}
          </button>
          <button
            class="px-2 py-0.5 text-xs font-semibold rounded transition-all w-10"
            :class="[
              field.type === 'property'
                ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200',
            ]"
            @click="updateType('property')"
          >
            {{ t('LIBRARIES.TEMPLATE_CONFIG.INPUT_TYPES.VAR') }}
          </button>
          <button
            class="px-2 py-0.5 text-xs font-semibold rounded transition-all w-10"
            :class="[
              field.type === 'fixed'
                ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200',
            ]"
            @click="updateType('fixed')"
          >
            {{ t('LIBRARIES.TEMPLATE_CONFIG.INPUT_TYPES.FIX') }}
          </button>
        </div>
      </div>

      <!-- Variable selector (Var) -->
      <div v-if="field.type === 'property'">
        <select
          :value="field.field"
          class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          @change="updateFieldValue('field', $event.target.value)"
        >
          <option value="">
            {{ t('LIBRARIES.TEMPLATE_CONFIG.PLACEHOLDERS.SELECT_FIELD') }}
          </option>
          <option
            v-for="availField in availableFields"
            :key="availField.value"
            :value="availField.value"
          >
            {{ availField.label }}
          </option>
        </select>
      </div>

      <!-- AI Prompt input (AI) -->
      <div v-if="field.type === 'prompt'">
        <textarea
          :value="field.prompt"
          :placeholder="
            t('LIBRARIES.TEMPLATE_CONFIG.PLACEHOLDERS.AI_PROMPT', {
              field: fieldLabel.toLowerCase(),
            })
          "
          maxlength="100"
          class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent resize-none"
          rows="2"
          @input="updateFieldValue('prompt', $event.target.value)"
        />
      </div>

      <!-- Fixed value input (Fix) -->
      <div v-if="field.type === 'fixed'">
        <input
          :value="field.value"
          type="text"
          :placeholder="
            t('LIBRARIES.TEMPLATE_CONFIG.PLACEHOLDERS.FIXED_VALUE', {
              field: fieldLabel.toLowerCase(),
            })
          "
          class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          @input="updateFieldValue('value', $event.target.value)"
        />
      </div>
    </div>
  </div>
</template>
